import ChildTable from "@/components/BaseTable/ChildTable";
import CustomButton from "@/components/common/Button";
import CommonModal from "@/components/common/CommonModal";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { capitalizeText } from "@/lib/capitalizeText";
import { generateTimestamp } from "@/lib/generateTimestamp";
import { SchemaService, UploadService } from "@/services/APIs";
import DropdownService from "@/services/APIs/DropdownService";
import {
  CustomChildTableProps,
  ModalState,
  TableRecord,
} from "@/utils/types/baseTable";
import { ProColumns } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Dropdown, MenuProps, Popconfirm, message } from "antd";
import { Edit2, FileText, Plus, Trash } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Constants
const DEFAULT_PAGE_SIZE = 50;
const MODAL_WIDTH = 800;
const DEFAULT_RELATION_DISPLAY_SCHEMAS: Record<string, string> = {
  employee: "PTS Emp",
  member: "PTS Emp",
  designation: "PTS Designation",
};

function isDateLikeGridField(fieldName: string, fieldTitle: string): boolean {
  const n = fieldName.trim().toLowerCase();
  const t = fieldTitle.trim().toLowerCase();
  return n.includes("date") || t.includes("date");
}

/** Renders API date/datetime strings as DD-MM-YYYY for table display. */
function formatDateDdMmYyyy(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, y, mo, d] = m;
    return `${d}-${mo}-${y}`;
  }
  return s;
}

const isUUID = (val: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    val.trim()
  );

function isAttachmentLikeGridField(
  fieldName: string,
  fieldTitle: string
): boolean {
  const n = fieldName.trim().toLowerCase();
  const t = fieldTitle.trim().toLowerCase();
  return (
    n.includes("attachment") ||
    t.includes("attachment") ||
    n.includes("file_upload") ||
    t.includes("file upload")
  );
}

/** Normalize stored file IDs: JSON array string, UUID[], or single UUID. */
function parseAttachmentIdsFromCell(value: unknown): string[] {
  if (value === null || value === undefined || value === "") {
    return [];
  }
  let v: unknown = value;

  if (typeof v === "string") {
    const trimmed = v.trim();
    if (trimmed === "" || trimmed === "-") {
      return [];
    }
    if (isUUID(trimmed)) {
      return [trimmed];
    }
    if (trimmed.startsWith("[")) {
      try {
        v = JSON.parse(trimmed);
      } catch {
        return [];
      }
    } else {
      return [];
    }
  }

  if (Array.isArray(v)) {
    const out: string[] = [];
    for (const item of v) {
      if (typeof item === "string" && isUUID(item)) {
        out.push(item);
      } else if (typeof item === "object" && item !== null && "id" in item) {
        const id = String((item as { id: unknown }).id);
        if (isUUID(id)) {
          out.push(id);
        }
      }
    }
    return out;
  }

  if (typeof v === "object" && v !== null && "id" in v) {
    const id = String((v as { id: unknown }).id);
    return isUUID(id) ? [id] : [];
  }

  return [];
}

function normalizeFileOpenUrl(url: string): string {
  if (url.startsWith("http://djangoproject.universal-software.in")) {
    return url.replace("http://", "https://");
  }
  return url;
}

/** Fetches file metadata by ID and shows named links (child table / expense attachments). */
function AttachmentIdsCell({ raw }: { raw: unknown }) {
  const ids = useMemo(() => parseAttachmentIdsFromCell(raw), [raw]);
  const queries = useQueries({
    queries: ids.map(id => ({
      queryKey: ["file-by-id", id] as const,
      queryFn: () => UploadService.getFileById({ id }),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  if (ids.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {ids.map((id, i) => {
        const q = queries[i];
        const data = q?.data as
          | {
              file_path?: string;
              url?: string;
              file_original_name?: string;
              name?: string;
            }
          | undefined;
        const urlRaw = data?.file_path ?? data?.url;
        const url = urlRaw ? normalizeFileOpenUrl(urlRaw) : undefined;
        const name =
          (data?.file_original_name ?? data?.name ?? "Open file") ||
          "Open file";

        if (q?.isLoading) {
          return (
            <span key={id} className="text-xs text-gray-400">
              Loading…
            </span>
          );
        }
        if (url) {
          return (
            <a
              key={id}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex max-w-[240px] items-center gap-1 text-sm underline"
              title={name}
            >
              <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="">{name}</span>
            </a>
          );
        }
        return (
          <span key={id} className="text-xs text-gray-500" title={id}>
            File unavailable
          </span>
        );
      })}
    </div>
  );
}

// Utility functions
const generateUniqueId = (): number => Math.floor(Math.random() * 1e12);

const parseSchemaJson = (schemaInfo: any) => {
  if (!schemaInfo) {
    return null;
  }

  try {
    return schemaInfo.json;
  } catch (error: any) {
    console.error("Error parsing schema JSON:", error);
  }
};

const getRelationDisplaySchema = (
  fieldName: string,
  fieldTitle: string,
  relationDisplaySchemas?: Record<string, string>
): string | undefined => {
  const normalizedFieldName = fieldName.trim().toLowerCase();
  const normalizedFieldTitle = fieldTitle.trim().toLowerCase();

  return (
    relationDisplaySchemas?.[normalizedFieldName] ??
    relationDisplaySchemas?.[fieldName] ??
    relationDisplaySchemas?.[fieldTitle] ??
    DEFAULT_RELATION_DISPLAY_SCHEMAS[normalizedFieldName] ??
    DEFAULT_RELATION_DISPLAY_SCHEMAS[normalizedFieldTitle]
  );
};

// Simple utility to extract only individual record fields (non-array fields)
// This function works with multiple child tables by excluding the current child table field
const extractIndividualRecordData = (
  formData: any,
  excludedFieldNames: string[] = []
): any => {
  const individualRecordData: any = {};
  const excludedFields = new Set(excludedFieldNames);

  // Preserve normal scalar/array values such as uploaded file IDs.
  // Exclude only actual nested child-table fields.
  Object.keys(formData).forEach(fieldName => {
    const fieldValue = formData[fieldName];
    if (!excludedFields.has(fieldName)) {
      individualRecordData[fieldName] = fieldValue;
    }
  });

  return individualRecordData;
};

// Custom hooks
const useTableData = (value?: any[]) => {
  const [tableData, setTableData] = useState<TableRecord[]>(value ?? []);

  useEffect(() => {
    if (value !== undefined) {
      setTableData(value);
    }
  }, [value]);

  return [tableData, setTableData] as const;
};

const useModalState = () => {
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    editingId: null,
    isEditMode: false,
  });

  const openModal = useCallback((editingId: string | null = null) => {
    setModalState({
      open: true,
      editingId,
      isEditMode: !!editingId,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      editingId: null,
      isEditMode: false,
    });
  }, []);

  return { modalState, openModal, closeModal };
};

const useFormManagement = () => {
  const [form, setForm] = useState(() => createForm({}));

  const createNewForm = useCallback((initialValues?: any) => {
    const newForm = createForm(initialValues ? { initialValues } : {});
    setForm(newForm);
    return newForm;
  }, []);

  return { form, createNewForm };
};

const useTableOperations = (
  tableData: TableRecord[],
  updateTableData: (data: TableRecord[]) => void,
  closeModal: () => void,
  excludedFieldNames: string[] = []
) => {
  const validateFormData = useCallback((formData: any): boolean => {
    if (!formData || Object.keys(formData).length === 0) {
      message.warning("Please fill in the required fields");
      return false;
    }
    return true;
  }, []);

  const handleSave = useCallback(
    async (form: any) => {
      try {
        const formData = form.getFormState().values;
        if (!validateFormData(formData)) {
          return;
        }

        // Extract only the individual record fields, excluding child table fields
        const individualRecordData = extractIndividualRecordData(
          formData,
          excludedFieldNames
        );

        const currentTimestamp = generateTimestamp();
        const newRecord: TableRecord = {
          ...individualRecordData,
          index: generateUniqueId(),
          created_on: currentTimestamp,
          updated_on: currentTimestamp,
        };

        updateTableData([...tableData, newRecord]);
        closeModal();
        message.success("Record saved successfully");
      } catch (error: any) {
        message.error("Failed to save record");
        console.error("Failed to save record:", error);
      }
    },
    [
      tableData,
      updateTableData,
      closeModal,
      validateFormData,
      excludedFieldNames,
    ]
  );

  const handleUpdate = useCallback(
    async (form: any, editingId: string) => {
      try {
        const formData = form.getFormState().values;
        if (!validateFormData(formData)) {
          return;
        }

        // Extract only the individual record fields, excluding child table fields
        const individualRecordData = extractIndividualRecordData(
          formData,
          excludedFieldNames
        );

        const currentTimestamp = generateTimestamp();
        const updatedData = tableData.map(item =>
          item.index === editingId
            ? {
                ...item,
                ...individualRecordData,
                updated_on: currentTimestamp,
              }
            : item
        );

        updateTableData(updatedData);
        closeModal();
        message.success("Record updated successfully");
      } catch (error: any) {
        message.error("Failed to update record");
        console.error("Failed to update record:", error);
      }
    },
    [
      tableData,
      updateTableData,
      closeModal,
      validateFormData,
      excludedFieldNames,
    ]
  );

  const handleDelete = useCallback(
    (index: string) => {
      const newData = tableData.filter(item => item.index !== index);
      updateTableData(newData);
      message.success("Record deleted successfully");
    },
    [tableData, updateTableData]
  );

  const handleBulkDelete = useCallback(
    async (selectedRowKeys: React.Key[]) => {
      if (selectedRowKeys.length === 0) {
        message.warning("No items selected for deletion");
        return;
      }

      try {
        const newData = tableData.filter(
          item => !selectedRowKeys.includes(item.index)
        );
        updateTableData(newData);
        message.success(
          `${selectedRowKeys.length} record(s) deleted successfully`
        );
        return newData;
      } catch (error: any) {
        message.error("Failed to delete records");
        console.error("Failed to delete records:", error);
      }
    },
    [tableData, updateTableData]
  );

  return {
    handleSave,
    handleUpdate,
    handleDelete,
    handleBulkDelete,
  };
};

export const CustomChildTable = (props: CustomChildTableProps) => {
  const {
    x_child,
    onTableDataChange,
    value,
    onChange,
    hideHeaderTitle,
    relationDisplaySchemas,
  } = props;

  // State management
  const [tableData, setTableData] = useTableData(value);
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 50);
  const urlPageSize = parseInt(
    searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE),
    50
  );
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Custom hooks
  const { modalState, openModal, closeModal } = useModalState();
  const { form, createNewForm } = useFormManagement();

  // API hooks
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", x_child],
    queryFn: () => SchemaService.getColumnGridData(x_child ?? ""),
    enabled: !!x_child,
  });

  const { data: schemaInfo } = useQuery({
    queryKey: ["schema", x_child],
    queryFn: () => SchemaService.getSchemaByName(x_child),
    enabled: !!x_child,
  });

  // Fetch dropdown data for relation display (id -> name)
  const schemaNamesToFetch = useMemo(() => {
    const explicitSchemaNames = relationDisplaySchemas
      ? (Object.values(relationDisplaySchemas) as string[])
      : [];

    const inferredSchemaNames =
      columnGridData?.grid
        ?.map((field: { field_title: string; field_name: string }) =>
          getRelationDisplaySchema(
            field.field_name,
            field.field_title,
            relationDisplaySchemas
          )
        )
        .filter(
          (schemaName: string | undefined): schemaName is string => !!schemaName
        ) ?? [];

    return [...new Set([...explicitSchemaNames, ...inferredSchemaNames])];
  }, [columnGridData, relationDisplaySchemas]);
  const dropdownQueries = useQueries({
    queries: schemaNamesToFetch.map((schemaName: string) => ({
      queryKey: ["dropdown-display", schemaName],
      queryFn: () => DropdownService.getDropdownData(schemaName),
      enabled: !!schemaName,
    })),
  });
  const schemaToOptionsMap = useMemo(() => {
    const map: Record<string, { value: string; label: string }[]> = {};
    schemaNamesToFetch.forEach((schemaName: string, idx: number) => {
      const result = dropdownQueries[idx]?.data;
      map[schemaName] = Array.isArray(result) ? result : [];
    });
    return map;
  }, [schemaNamesToFetch, dropdownQueries]);

  // Computed values
  const total = useMemo(() => tableData.length, [tableData]);
  const schemaJson = useMemo(() => parseSchemaJson(schemaInfo), [schemaInfo]);
  const headerTitle = useMemo(
    () =>
      hideHeaderTitle ? "" : `Selected ${capitalizeText(x_child ?? "")} List`,
    [x_child, hideHeaderTitle]
  );
  const nestedChildFieldNames = useMemo(() => {
    const properties = schemaJson?.schema?.properties ?? schemaJson?.properties;

    if (!properties || typeof properties !== "object") {
      return [];
    }

    return Object.entries(properties)
      .filter(([, value]) => {
        const propertyValue = value as Record<string, unknown>;
        return propertyValue?.["x-component"] === "CustomChildTable";
      })
      .map(([key]) => key);
  }, [schemaJson]);

  // Callbacks
  const updateTableData = useCallback(
    (newData: TableRecord[]) => {
      setTableData(newData);
      onChange?.(newData);
      onTableDataChange?.(newData);
    },
    [setTableData, onChange, onTableDataChange]
  );

  const { handleSave, handleUpdate, handleDelete, handleBulkDelete } =
    useTableOperations(
      tableData,
      updateTableData,
      closeModal,
      nestedChildFieldNames
    );

  const handleEdit = useCallback(
    (index: string) => {
      const record = tableData.find(item => item.index === index);
      if (record) {
        createNewForm(record);
        openModal(index);
      }
    },
    [tableData, createNewForm, openModal]
  );

  const handleAddNew = useCallback(() => {
    createNewForm();
    openModal();
  }, [createNewForm, openModal]);

  const handlePageChange = useCallback((newPage: number, newSize?: number) => {
    setPage(newPage);
    setPageSize(newSize ?? DEFAULT_PAGE_SIZE);
  }, []);

  const handleBulkDeleteWithState = useCallback(async () => {
    setIsBulkDeleting(true);
    try {
      await handleBulkDelete(selectedRowKeys);
      setSelectedRowKeys([]);
    } finally {
      setIsBulkDeleting(false);
    }
  }, [handleBulkDelete, selectedRowKeys]);

  // Memoized components
  const columns: ProColumns<TableRecord>[] = useMemo(() => {
    const dynamicColumns: ProColumns<TableRecord>[] =
      columnGridData?.grid?.map(
        (field: { field_title: string; field_name: string }) => {
          const fieldName = field.field_name.toLowerCase();
          const displaySchema = getRelationDisplaySchema(
            field.field_name,
            field.field_title,
            relationDisplaySchemas
          );
          const options = displaySchema
            ? (schemaToOptionsMap[displaySchema] ?? [])
            : null;

          const col: ProColumns<TableRecord> = {
            title: field.field_title,
            dataIndex: fieldName,
            key: fieldName,
          };
          if (options && displaySchema) {
            col.render = (_: any, record: TableRecord) => {
              const rawValue =
                record[fieldName] ??
                record[field.field_name] ??
                record[fieldName.replace(/\s/g, "_")];

              if (rawValue === null || rawValue === "") {
                return "-";
              }

              if (typeof rawValue === "object") {
                const objectValue =
                  (rawValue as { id?: unknown; value?: unknown }).id ??
                  (rawValue as { id?: unknown; value?: unknown }).value;
                const objectLabel =
                  (rawValue as { label?: unknown; name?: unknown }).label ??
                  (rawValue as { label?: unknown; name?: unknown }).name;

                if (objectLabel) {
                  return String(objectLabel);
                }

                if (objectValue !== undefined && objectValue !== null) {
                  const matchedOption = options.find(
                    (opt: { value?: string; label?: string }) =>
                      String(opt?.value) === String(objectValue)
                  );
                  return matchedOption?.label ?? String(objectValue);
                }
              }

              const option = options.find(
                (opt: { value?: string; label?: string }) =>
                  String(opt?.value) === String(rawValue)
              );
              return option?.label ?? rawValue;
            };
          } else if (isDateLikeGridField(field.field_name, field.field_title)) {
            col.render = (_: unknown, record: TableRecord) => {
              const raw =
                record[fieldName] ??
                record[field.field_name] ??
                record[field.field_name.replace(/\s/g, "_")];
              return formatDateDdMmYyyy(raw);
            };
          } else if (
            isAttachmentLikeGridField(field.field_name, field.field_title)
          ) {
            col.render = (_: unknown, record: TableRecord) => {
              const raw =
                record[fieldName] ??
                record[field.field_name] ??
                record[field.field_name.replace(/\s/g, "_")];
              return <AttachmentIdsCell raw={raw} />;
            };
          }
          return col;
        }
      ) ?? [];

    return [
      ...dynamicColumns,
      {
        title: "Actions",
        width: 100,
        valueType: "option",
        key: "actions",
        render: (_: any, record: TableRecord) => [
          <CustomButton
            key="edit"
            icon={<Edit2 size={16} />}
            type="text"
            onClick={() => handleEdit(record.index)}
            title="Edit"
          />,
          <Popconfirm
            key="delete"
            title="Are you sure you want to delete this item?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record.index)}
          >
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              title="Delete"
            />
          </Popconfirm>,
        ],
      },
    ];
  }, [
    columnGridData,
    handleEdit,
    handleDelete,
    relationDisplaySchemas,
    schemaToOptionsMap,
  ]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    if (selectedRowKeys.length === 0) {
      return [];
    }

    return [
      {
        key: "delete",
        label: (
          <Popconfirm
            title={`Are you sure you want to delete ${selectedRowKeys.length} selected item(s)?`}
            onConfirm={handleBulkDeleteWithState}
            okText="Yes"
            cancelText="No"
          >
            <span className="flex items-center gap-2 text-sm text-red-500">
              <Trash size={16} />
              {isBulkDeleting
                ? "Deleting..."
                : `Delete (${selectedRowKeys.length})`}
            </span>
          </Popconfirm>
        ),
      },
    ];
  }, [selectedRowKeys.length, handleBulkDeleteWithState, isBulkDeleting]);

  const toolBarRender = useCallback(() => {
    const toolbarItems = [
      <CustomButton
        key="add"
        icon={<Plus size={16} />}
        onClick={handleAddNew}
        title="Add New Record"
      >
        Add New
      </CustomButton>,
    ];

    if (selectedRowKeys.length > 0) {
      toolbarItems.push(
        <Dropdown key="actions" menu={{ items: menuItems }}>
          <CustomButton
            title={`Bulk Actions (${selectedRowKeys.length} selected)`}
          >
            Actions ({selectedRowKeys.length})
          </CustomButton>
        </Dropdown>
      );
    }

    return toolbarItems;
  }, [handleAddNew, menuItems, selectedRowKeys.length]);

  const handleModalOk = useCallback(() => {
    if (modalState.isEditMode && modalState.editingId) {
      handleUpdate(form, modalState.editingId);
    } else {
      handleSave(form);
    }
  }, [
    modalState.isEditMode,
    modalState.editingId,
    handleUpdate,
    handleSave,
    form,
  ]);

  return (
    <>
      <ChildTable<TableRecord>
        columns={columns}
        dataSource={tableData}
        loading={!tableData}
        page={page}
        pageSize={pageSize}
        total={total}
        rowKey="index"
        headerTitle={headerTitle}
        toolBarRender={toolBarRender}
        onPageChange={handlePageChange}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />

      <CommonModal
        open={modalState.open}
        title={modalState.isEditMode ? "Edit Item" : "Add New Item"}
        width={MODAL_WIDTH}
        okText={modalState.isEditMode ? "Update" : "Save"}
        cancelText="Cancel"
        onOk={handleModalOk}
        onCancel={closeModal}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        key={`${modalState.isEditMode ? "edit" : "add"}-${modalState.editingId ?? "new"}`}
      >
        {schemaJson ? (
          <SchemaFormView
            key={`form-${modalState.isEditMode ? "edit" : "add"}-${modalState.editingId ?? "new"}`}
            schemaJson={schemaJson}
            form={form}
          />
        ) : (
          <LoadingSpinner />
        )}
      </CommonModal>
    </>
  );
};
